import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { type FileRejection, useDropzone } from "react-dropzone";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Alert, List, ListItem, ListItemSecondaryAction, ListItemText, Zoom } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Avatar,
  Divider,
  Button,
  Stack,
  Typography,
  useFinanceableTheme,
  Sheet,
  Tooltip,
  IconButton,
  Link,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Input,
} from "@financeable-com-au/financeable-ui";
// @ts-expect-error: ignoring as it is existing js file
import { v4 as uuidv4 } from "uuid";
import { ICurrentTemplate } from "@/store/slices/types/templatesSlice";
// @ts-expect-error: ignoring as it is existing js file
import { viewFile } from "@/components/Utils/helperFunction";
import { MiniLoader } from "@/components/Loader/Loader";
import {
  DOCUMENT_TEMPLATE_EMPTY_STATE,
  ERRORS_MESSAGE,
  FILE_ACCEPTANCE_TYPES,
  SECTION_TYPE,
  SUCCESS_MESSAGE,
} from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { removeAssetInvoice } from "@/store/slices/applicationFormSlice";
import { type Document } from "@/store/slices/types/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse } from "@/utils/interface";
import { type MissingRequiredField, NO_MISSING_REQUIRED_FIELDS } from "@/utils/requiredFieldRules";
import MissingRequiredFields from "@/components/CommonComponents/MissingRequiredFields";

interface UploadedFile {
  file: File;
  id: string;
  fileUrl?: string;
}

type SectionType = (typeof SECTION_TYPE)[keyof typeof SECTION_TYPE];

interface FileUploadDropzoneProps {
  isDisabled?: boolean;
  saveFilesHandler: (files: File[]) => void;
  isLoading?: boolean;
  isAssetDocumentUploadLoading?: boolean;
  isFetchingPreviewDocument?: boolean;
  isMultiple?: boolean;
  fileData?: ICurrentTemplate | Document;
  previewDocumentHandler?: () => void;
  onRequestInvoice?: (email: string) => Promise<boolean | void> | boolean | void;
  isRequestInvoiceLoading?: boolean;
  /** Fields still to be filled in before an invoice can be requested. Empty means good to go. */
  requestInvoiceMissingFields?: MissingRequiredField[];
  sectionType: SectionType;
}

interface ViewDocumentTemplateFilesProps {
  previewDocumentHandler: () => void;
  isFetchingPreviewDocument: boolean;
  fileData: ICurrentTemplate | Document;
  files: UploadedFile[];
  isLoading: boolean;
}

interface DropZoneStackStyle {
  direction: "row" | "column";
  alignItems: "center" | "flex-start" | "flex-end" | "stretch" | "baseline";
  justifyContent: "center" | "flex-start" | "flex-end" | "space-between" | "space-around" | "space-evenly";
  rowGap: number;
  maxWidth?: number;
  dropZoneStackStyleRowGap: number;
}

interface ViewAssetInvoiceFilesProps {
  fileData: Document;
}

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 5; // 5MB
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ACCEPTED_FILE_TYPE_LABELS: Record<SectionType, string> = {
  [SECTION_TYPE.DOCUMENT_TEMPLATE]: "Word file (.doc, .docx)",
  [SECTION_TYPE.ASSET_INVOICE]: "PDF or XLSX file",
};

const fileSizeLimitMessage = `Each file must be less than ${MAX_FILE_SIZE_MB} MB.`;

const getUnsupportedFileTypeMessage = (sectionType: SectionType) =>
  `Unsupported file type. Please upload a ${ACCEPTED_FILE_TYPE_LABELS[sectionType]}.`;

const getDragRejectionMessage = (sectionType: SectionType) =>
  `Please upload a ${ACCEPTED_FILE_TYPE_LABELS[sectionType]} under ${MAX_FILE_SIZE_MB} MB.`;

const getFileRejectionMessage = (fileRejections: FileRejection[], sectionType: SectionType) => {
  if (!fileRejections.length) return "";

  const hasOversizedFile = fileRejections.some(({ errors }) => errors.some((error) => error.code === "file-too-large"));
  if (hasOversizedFile) return fileSizeLimitMessage;

  const hasUnsupportedFile = fileRejections.some(({ errors }) =>
    errors.some((error) => error.code === "file-invalid-type"),
  );
  if (hasUnsupportedFile) return getUnsupportedFileTypeMessage(sectionType);

  return "Some files could not be uploaded. Please check the file type and size.";
};

function mimeTypeForDocumentTemplateFileName(fileName: string): string {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".doc")) return "application/msword";
  return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
}

const isPdfUrl = (url?: string): boolean => {
  if (!url) return false;
  return url.split("?")[0].toLowerCase().endsWith(".pdf");
};

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Holds the uploaded file's slot, laid out like its row, and points the user at the dropzone above. */
const NoUploadedFile = () => (
  <Sheet variant="outlined" borderRadius="md" sx={{ px: 4, py: 3 }}>
    <Stack direction="row" alignItems="center" columnGap={4}>
      <Avatar>
        <DescriptionIcon fontSize="small" />
      </Avatar>
      <Stack direction="column" rowGap={1}>
        <Typography level="title-sm">{DOCUMENT_TEMPLATE_EMPTY_STATE.TITLE}</Typography>
        <Typography level="body-sm" textColor="text.tertiary">
          {DOCUMENT_TEMPLATE_EMPTY_STATE.DESCRIPTION}
        </Typography>
      </Stack>
    </Stack>
  </Sheet>
);

const ViewDocumentTemplateFiles = ({
  previewDocumentHandler,
  isFetchingPreviewDocument,
  fileData,
  files,
  isLoading,
}: ViewDocumentTemplateFilesProps) => {
  const isPDFFile = fileData && "body" in fileData ? isPdfUrl(fileData.body) : false;
  return (
    <Stack flexDirection="column" rowGap={4}>
      {isPDFFile && (
        <Alert
          severity="info"
          sx={{
            padding: "2px 8px",
            fontSize: "0.8rem",
            minHeight: "unset",
            lineHeight: "1.2",
            "& .MuiAlert-icon": { fontSize: "1.2rem" },
          }}
        >
          <Typography level="body-sm">
            Upload a Word file (.doc, .docx) to use as your document template. PDF files are not supported.
          </Typography>
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography level="body-lg">Uploaded File</Typography>
        {fileData && "body" in fileData && (
          <Stack direction="row" alignItems="center">
            <LoadingButton
              color="primary"
              loadingPosition="end"
              variant="outlined"
              size="small"
              onClick={previewDocumentHandler}
              disabled={isFetchingPreviewDocument || !("body" in fileData)}
            >
              {isFetchingPreviewDocument ? "Loading..." : "Preview document"}
            </LoadingButton>
            <Tooltip
              title={
                <Stack padding={2} flexDirection="column" rowGap={2}>
                  <Typography>
                    {!("body" in fileData) ? "Upload document to preview" : "Preview document with sample data"}
                  </Typography>
                </Stack>
              }
            >
              <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                <InfoIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      {files.length > 0 && !isLoading && (
        <Sheet variant="outlined" borderRadius="md" sx={{ padding: 0 }}>
          <List dense disablePadding>
            {files.map((f, idx) => (
              <React.Fragment key={f.id}>
                <ListItem
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Avatar color="primary" sx={{ mr: 4 }}>
                    <DescriptionIcon fontSize="small" />
                  </Avatar>

                  <ListItemText primary={<Typography level="body-md">{f.file.name}</Typography>} />
                  <ListItemSecondaryAction>
                    <Button
                      variant="secondary"
                      onClick={() => viewFile(f.fileUrl || "")}
                      size="md"
                      disabled={isPDFFile}
                    >
                      View File
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                {idx < files.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Sheet>
      )}

      {files.length === 0 && !isLoading && <NoUploadedFile />}

      {isLoading && <MiniLoader />}
    </Stack>
  );
};

const ViewAssetInvoiceFiles = ({ fileData }: ViewAssetInvoiceFilesProps) => {
  const dispatch = useDispatch();
  const theme = useFinanceableTheme();
  const [isLoading, setIsLoading] = useState(false);

  const handle = {
    downloadFile: () => {
      viewFile(fileData?.url);
    },
    removeFile: async () => {
      setIsLoading(true);

      const removeDocPayload = {
        documentId: fileData?._id,
        assetId: fileData?.asset,
        applicationId: fileData?.application,
      };

      await dispatch(removeAssetInvoice(removeDocPayload)).then((res: IDispatchResponse) => {
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || SUCCESS_MESSAGE.fileRemovedSuccess,
              intent: "success",
            }),
          );
        }
        setIsLoading(false);
      });
    },
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      border={`1px solid #CCCCCC`}
      bgcolor="#ffffff"
      borderRadius="8px"
      p={1.5}
      sx={{ cursor: "pointer", "&:hover": { bgcolor: "#E8F5E9" } }}
      onClick={handle.downloadFile}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <DescriptionIcon sx={{ color: "green" }} />
        <Typography level="body-lg" color="success">
          {fileData?.fileName}
        </Typography>
      </Box>

      {/* Cancel button */}
      {isLoading ? (
        <CircularProgress size="sm" />
      ) : (
        <IconButton
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handle.removeFile();
          }}
          disabled={isLoading}
          style={{
            backgroundColor: "transparent",
          }}
        >
          <DeleteIcon color="error" />
        </IconButton>
      )}
    </Box>
  );
};

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  isDisabled = false,
  isLoading = false,
  isAssetDocumentUploadLoading = false,
  isFetchingPreviewDocument = false,
  isMultiple = false,
  saveFilesHandler,
  fileData,
  previewDocumentHandler,
  onRequestInvoice,
  isRequestInvoiceLoading = false,
  requestInvoiceMissingFields = NO_MISSING_REQUIRED_FIELDS,
  sectionType,
}) => {
  const theme = useFinanceableTheme();

  const fontSize = sectionType === SECTION_TYPE.ASSET_INVOICE ? "24" : "48";
  const isDocumentTemplate = sectionType === SECTION_TYPE.DOCUMENT_TEMPLATE;
  const isAssetInvoice = sectionType === SECTION_TYPE.ASSET_INVOICE;

  // Fix: Safely access fileData properties based on sectionType
  let fileUrl: string | undefined;
  if (isAssetInvoice) {
    // fileData is expected to be of type Document (has url)
    fileUrl = (fileData as Document | undefined)?.url;
  } else {
    // fileData is expected to be of type ICurrentTemplate (has body)
    fileUrl = (fileData as ICurrentTemplate | undefined)?.body;
  }

  const files = fileUrl
    ? [
        fileUrl
          ? {
              file: new File([fileUrl], fileData?.name || "template.docx", {
                type: mimeTypeForDocumentTemplateFileName(fileData?.name || "template.docx"),
              }),
              id: fileData?._id || "",
              fileUrl: fileUrl,
            }
          : undefined,
      ].filter(Boolean)
    : [];

  const [message, setMessage] = useState<string>("");
  const [invoiceRequestEmail, setInvoiceRequestEmail] = useState("");
  const [invoiceRequestEmailError, setInvoiceRequestEmailError] = useState("");

  const trimmedInvoiceRequestEmail = invoiceRequestEmail.trim();
  const hasMissingInvoiceRequirements = requestInvoiceMissingFields.length > 0;
  const canRequestInvoice =
    Boolean(trimmedInvoiceRequestEmail) && !isDisabled && !isRequestInvoiceLoading && !hasMissingInvoiceRequirements;

  const handleRequestInvoice = async () => {
    if (!canRequestInvoice || !onRequestInvoice) return;

    if (!isValidEmail(trimmedInvoiceRequestEmail)) {
      setInvoiceRequestEmailError("Please enter a valid email address.");
      return;
    }

    setInvoiceRequestEmailError("");
    const isRequestSent = await onRequestInvoice(trimmedInvoiceRequestEmail);
    if (isRequestSent !== false) {
      setInvoiceRequestEmail("");
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const rejectionMessage = getFileRejectionMessage(fileRejections, sectionType);
      if (rejectionMessage) {
        setMessage(rejectionMessage);
        return;
      }

      const oversizedFiles = acceptedFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        setMessage(fileSizeLimitMessage);
        return;
      }

      if (files.length > MAX_FILES) {
        setMessage("You can upload a maximum of 5 files. Please delete one to add another.");
        return;
      }

      const newFiles: UploadedFile[] = acceptedFiles.slice(0, MAX_FILES - files.length).map((file) => ({
        file,
        id: uuidv4(),
      }));

      saveFilesHandler(newFiles.map((file) => file.file)); // Enable it when we need to save to backend

      setMessage("");
    },
    [files, saveFilesHandler, sectionType],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    disabled: isAssetDocumentUploadLoading,
    multiple: isMultiple,
    accept: FILE_ACCEPTANCE_TYPES[sectionType] as unknown as import("react-dropzone").Accept,
    maxSize: MAX_FILE_SIZE_BYTES,
  });

  const isfileUploadEnabled = (files?.length ?? 0) <= 1;
  const dropZoneBgColor = isfileUploadEnabled ? theme.palette.neutral[50] : theme.palette.neutral[100];

  let dropZoneStackStyle: DropZoneStackStyle = {
    direction: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: 2,
    maxWidth: 700,
    dropZoneStackStyleRowGap: 10,
  };

  let dropZoneText = <Typography level="body-md">Drag & drop or click to upload</Typography>;

  if (isAssetInvoice) {
    dropZoneStackStyle = {
      direction: "row",
      alignItems: "flex-start",
      justifyContent: "center",
      rowGap: 2,
      maxWidth: 1000,
      dropZoneStackStyleRowGap: 1,
    };
    dropZoneText = isAssetDocumentUploadLoading ? (
      <Typography level="body-md">Document is uploading...</Typography>
    ) : (
      <Typography level="body-md">
        Drag and Drop Invoice file here or
        <Link
          component="button"
          sx={{ color: theme.palette.primary[200], cursor: "pointer", textDecoration: "underline", ml: 1 }}
          onClick={(e) => e.preventDefault()}
        >
          choose file
        </Link>
      </Typography>
    );
  }

  let assetInvoiceDropZoneContent: React.ReactNode = (
    <>
      Drag and Drop Invoice file here or{" "}
      <Link
        component="button"
        sx={{ color: theme.palette.primary[200], cursor: "pointer", textDecoration: "underline" }}
        onClick={(e) => e.preventDefault()}
      >
        choose file
      </Link>
    </>
  );

  if (isDragReject) {
    assetInvoiceDropZoneContent = getDragRejectionMessage(sectionType);
  } else if (isDragActive) {
    assetInvoiceDropZoneContent = "Drop the files here...";
  } else if (isAssetDocumentUploadLoading) {
    assetInvoiceDropZoneContent = "Document is uploading...";
  }

  let documentTemplateDropZoneContent: React.ReactNode = dropZoneText;
  if (isDragReject) {
    documentTemplateDropZoneContent = getDragRejectionMessage(sectionType);
  } else if (isDragActive) {
    documentTemplateDropZoneContent = "Drop the files here...";
  }

  return (
    <Stack
      width="100%"
      maxWidth={dropZoneStackStyle.maxWidth}
      direction="column"
      alignItems="stretch"
      alignContent="stretch"
      rowGap={dropZoneStackStyle.dropZoneStackStyleRowGap}
    >
      {(isDocumentTemplate || (isAssetInvoice && files?.length <= 0)) && (
        <>
          {isAssetInvoice && (
            <Stack direction="column" rowGap={2} width="100%">
              {/* Option 1: Request invoice by email */}
              <Sheet
                variant="outlined"
                borderRadius="md"
                padding={2.5}
                sx={{
                  bgcolor: theme.palette.neutral[50],
                  border: `1px solid ${theme.palette.neutral[200]}`,
                }}
              >
                <Stack direction="column" rowGap={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar color="primary" size="sm" sx={{ "--Avatar-size": "28px" }}>
                      <EmailIcon sx={{ fontSize: "1rem" }} />
                    </Avatar>
                    <Typography level="title-sm" style={{ fontWeight: 600 }}>
                      Request invoice by email
                    </Typography>
                  </Box>

                  <FormControl error={!!invoiceRequestEmailError}>
                    <Stack direction="row" alignItems="flex-start" columnGap={2} rowGap={2} sx={{ flexWrap: "wrap" }}>
                      <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                        <Input
                          type="email"
                          placeholder="supplier@example.com"
                          value={invoiceRequestEmail}
                          disabled={isDisabled || isRequestInvoiceLoading}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setInvoiceRequestEmail(event.target.value);
                            if (invoiceRequestEmailError) {
                              setInvoiceRequestEmailError("");
                            }
                          }}
                          onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              handleRequestInvoice();
                            }
                          }}
                        />
                        {invoiceRequestEmailError ? (
                          <FormHelperText>
                            <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                            {invoiceRequestEmailError}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Button
                        variant="primary"
                        size="md"
                        startDecorator={<EmailIcon sx={{ fontSize: "1rem" }} />}
                        loading={isRequestInvoiceLoading}
                        disabled={!canRequestInvoice || !onRequestInvoice}
                        onClick={handleRequestInvoice}
                      >
                        Request invoice
                      </Button>
                    </Stack>
                  </FormControl>

                  <MissingRequiredFields fields={requestInvoiceMissingFields} />
                </Stack>
              </Sheet>

              {/* OR Divider */}
              <Divider sx={{ color: theme.palette.neutral[500], fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.05em", my: 0.5 }}></Divider>

              {/* Option 2: Upload invoice directly */}
              <Stack direction="column" rowGap={1}>
                <Sheet
                  {...getRootProps()}
                  hasBoxShadow
                  borderRadius="md"
                  padding={2.5}
                  sx={{
                    border: `1px dashed ${theme.palette.neutral[400]}`,
                    ...(isfileUploadEnabled && { cursor: "pointer" }),
                    bgcolor: isDragActive ? theme.palette.primary[50] : dropZoneBgColor,
                    transition: "background-color 0.3s ease",
                    "&:hover": {
                      bgcolor: theme.palette.primary[50],
                    },
                  }}
                >
                  <Stack
                    direction={dropZoneStackStyle.direction || "column"}
                    alignItems={dropZoneStackStyle.alignItems || "center"}
                    justifyContent={dropZoneStackStyle.justifyContent || "center"}
                    rowGap={dropZoneStackStyle.rowGap}
                  >
                    <input {...getInputProps()} disabled={!isfileUploadEnabled || isDisabled} />
                    <UploadFileIcon color="primary" sx={{ fontSize: fontSize }} />
                    <Typography level="body-md">{assetInvoiceDropZoneContent}</Typography>
                  </Stack>
                </Sheet>

                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-xs" style={{ color: theme.palette.neutral[500] }}>
                    Files Supported: PDF, XLSX
                  </Typography>
                  <Typography level="body-xs" style={{ color: theme.palette.neutral[500] }}>
                    Max File Size: 5 MB
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          )}

          {isDocumentTemplate && (
            <>
              <Sheet
                {...getRootProps()}
                hasBoxShadow
                borderRadius="md"
                padding={3}
                sx={{
                  border: `1px dashed ${theme.palette.neutral[400]}`,
                  ...(isfileUploadEnabled && { cursor: "pointer" }),
                  bgcolor: isDragActive ? theme.palette.primary[50] : dropZoneBgColor,
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    bgcolor: theme.palette.primary[50],
                  },
                }}
              >
                <Stack
                  direction={dropZoneStackStyle.direction || "column"}
                  alignItems={dropZoneStackStyle.alignItems || "center"}
                  justifyContent={dropZoneStackStyle.justifyContent || "center"}
                  rowGap={dropZoneStackStyle.rowGap}
                  mt={1}
                >
                  <input {...getInputProps()} disabled={!isfileUploadEnabled || isDisabled} />
                  <UploadFileIcon color="primary" sx={{ fontSize: fontSize }} />
                  <Typography level="body-lg" gutterBottom>
                    {documentTemplateDropZoneContent}
                  </Typography>
                  <Typography level="body-sm">
                    You can upload a Word file (.doc, .docx). Max size: {MAX_FILE_SIZE_MB} MB.
                  </Typography>
                </Stack>
              </Sheet>
            </>
          )}
        </>
      )}

      {message && (
        <Zoom in>
          <Alert severity="warning" sx={{ mt: 3 }}>
            {message}
          </Alert>
        </Zoom>
      )}

      {isDocumentTemplate && (
        <ViewDocumentTemplateFiles
          previewDocumentHandler={previewDocumentHandler || (() => {})}
          isFetchingPreviewDocument={isFetchingPreviewDocument}
          fileData={fileData as ICurrentTemplate}
          files={files as UploadedFile[]}
          isLoading={isLoading}
        />
      )}

      {isAssetInvoice && files?.length > 0 && fileData && <ViewAssetInvoiceFiles fileData={fileData as Document} />}
    </Stack>
  );
};

export default FileUploadDropzone;
